/**
 * Project-prefixed download names for the Deliverables tree.
 *
 * Drive keeps the short deliverable names ("SSD Summary 28-41.8.pdf"); the project
 * name is added only when a file leaves the platform. The client decides *whether*
 * to prefix (is the item under the project's `Deliverables/` folder?) and the
 * backend download routes apply the prefix to the advertised file name / zip
 * entries when the client sends `name_prefix`.
 *
 * Mirrors `Backend/123bimlive/utils/download_names.py` — keep the two in sync so
 * a client-side `<a download>` name and the server's Content-Disposition agree.
 * Spec: docs/superpowers/specs/2026-09-08-deliverables-naming-and-pdf.md §3.
 *
 * "Under Deliverables" rule: the caller passes the breadcrumb of the folder that
 * CONTAINS the item (root → containing folder, item itself excluded). The item is
 * under Deliverables when any of those segments normalises to `DELIVERABLES`
 * (case/punctuation-insensitive, same normaliser as the RSA folder helpers). This
 * covers any depth (Reports/docx/videos/csv/Shapefiles/dxf and deeper) and the
 * scoped Deliverables explorer, whose tree root *is* the Deliverables folder.
 */

export const MAX_PREFIX_LENGTH = 80;
export const DELIVERABLES_ZIP_BASENAME = 'deliverables.zip';

const ILLEGAL_CHARS_RE = /[\\/:*?"<>|]/g;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_RE = /[\x00-\x1f\x7f]/g;
const WHITESPACE_RE = /\s+/g;

/** Same normaliser as `normalizeFolderName` in rsaSharedConstants (kept local so this stays dependency-free). */
export const normalizeSegmentName = (value) =>
  String(value ?? '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, '');

const segmentName = (segment) => {
  if (segment == null) return '';
  if (typeof segment === 'string') return segment;
  return segment.name ?? '';
};

/**
 * @param {Array<string|{name?: string}>} pathSegments breadcrumb of the containing
 *   folder, root first (item itself excluded)
 * @returns {boolean}
 */
export const isUnderDeliverables = (pathSegments) => {
  if (!Array.isArray(pathSegments)) return false;
  return pathSegments.some((segment) => normalizeSegmentName(segmentName(segment)) === 'DELIVERABLES');
};

/**
 * File-system-safe prefix, '' when nothing usable was given.
 * `/ \ : * ? " < > |` → `-`; control chars dropped; whitespace collapsed; trimmed;
 * capped at MAX_PREFIX_LENGTH (trimmed again after the cut).
 */
export const sanitizeDownloadNamePrefix = (raw) => {
  if (raw == null) return '';
  let text = String(raw);
  text = text.replace(WHITESPACE_RE, ' ');
  text = text.replace(CONTROL_CHARS_RE, '');
  text = text.replace(ILLEGAL_CHARS_RE, '-');
  text = text.replace(WHITESPACE_RE, ' ').trim();
  if (text.length > MAX_PREFIX_LENGTH) {
    text = text.slice(0, MAX_PREFIX_LENGTH).replace(/\s+$/, '');
  }
  return text;
};

/**
 * The prefix to send as `name_prefix` for a download, or '' when the download
 * should be left unprefixed (not under Deliverables, or no project name).
 *
 * @param {{projectName: string, pathSegments: Array<string|{name?: string}>}} args
 * @returns {string}
 */
export const buildDeliverableDownloadPrefix = ({ projectName, pathSegments } = {}) => {
  if (!isUnderDeliverables(pathSegments)) return '';
  return sanitizeDownloadNamePrefix(projectName);
};

/** `"<prefix> <name>"` with a prefix, else `name` unchanged. */
export const prefixedDownloadName = (prefix, name) => (prefix ? `${prefix} ${name}` : name);

/** Zip name the backend picks when `name_prefix` is set and no `zip_name` is given. */
export const deliverablesZipName = (prefix) => (prefix ? `${prefix} ${DELIVERABLES_ZIP_BASENAME}` : '');

/**
 * `filename` from a Content-Disposition header, or null. Used to honour the name
 * the backend chose for a zip when the header is readable (same-origin via
 * CloudFront); cross-origin dev setups fall back to `deliverablesZipName`.
 */
export const fileNameFromContentDisposition = (headerValue) => {
  const value = String(headerValue ?? '');
  if (!value) return null;
  const utf8 = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch (e) {
      // fall through to the plain form
    }
  }
  const quoted = value.match(/filename="([^"]*)"/i);
  if (quoted) return quoted[1];
  const bare = value.match(/filename=([^;]+)/i);
  return bare ? bare[1].trim() : null;
};
